/**
 * Saves.
 *
 * Kept out of `src/sim` deliberately: serialisation is simulation logic and has
 * to run headless, but *where the bytes land* is a platform detail. The layering
 * test enforces the split.
 *
 * A campaign is *two* worlds — the realm, and the capital city standing in it —
 * so a save is a small envelope holding both rather than a single `SaveGame`.
 * The envelope is versioned separately from the worlds inside it, and a save
 * written before the envelope existed is read as a lone world, which is what
 * keeps every run made by an earlier build loadable.
 *
 * ## Two backends, one boundary
 *
 * This file used to be six calls to `localStorage`. It is now the same file
 * with the destination pulled out into `savestore.ts`, because the desktop
 * build keeps saves as real files beside the executable and a browser cannot.
 * A campaign on the 190-province board serialises to something like 3.7 MB
 * against a quota of five, so the game was already at the wall.
 *
 * The backend is chosen once, at boot, by `initStorage()`. **In a browser it is
 * `localStorage` under the same key it has always used**, which is what keeps
 * `npm run dev` and `npm run shots` — the project's only way of judging its own
 * art — behaving exactly as they did. The file backend is an addition behind
 * this boundary, never a replacement.
 *
 * Every function below is synchronous, and stays that way. The callers are a
 * keypress, a menu click and an autosave inside the frame loop; none of them can
 * be made to await. `FileStore` keeps the slots in memory and mirrors them to
 * disk on a queue. `flushSaves()` is how you find out the disk agreed.
 *
 * Nothing about the save *format* changed here, and `SAVE_VERSION` is untouched.
 * This is about where bytes go, not what they are.
 */

import { loadWorld, saveWorld, SAVE_KEY, type SaveGame } from '../sim/save.ts';
import type { World } from '../sim/world.ts';
import { desktopSaveDir, isDesktop, type DesktopSaveDir } from './desktop.ts';
import {
  DEFAULT_SLOT,
  FileStore,
  LocalStore,
  isSlot,
  migrateToFiles,
  type SaveStore,
  type WriteResult,
} from './savestore.ts';

export { SLOT_COUNT, DEFAULT_SLOT } from './savestore.ts';

/**
 * 1 → 2: a campaign is the realm plus *n* towns.
 *
 * The envelope is versioned separately from the worlds inside it precisely so
 * that "how many worlds is a game" can change without touching `SAVE_VERSION`,
 * and this is the first time it has. A version-1 envelope has one `city` and no
 * `towns` array, which loads as a campaign whose only settlement is its
 * capital — which is what every campaign was.
 *
 * The size of this is worth knowing before writing one: a settlement world is
 * about **0.53 MiB of JSON text** and the realm about **0.64 MiB**
 * (`npm run towncost`). `localStorage` counts its quota in UTF-16, so a
 * browser save is roughly double that in quota terms and the five-megabyte wall
 * arrives at about **two towns beyond the capital**. `write` returns
 * `{ ok: false }` on quota rather than throwing, and `main.ts` already prints
 * the reason; the desktop backend has no such wall, which is the honest answer
 * for a player who wants four.
 */
const ENVELOPE_VERSION = 2;

interface SaveEnvelope {
  envelope: number;
  savedAt: number;
  /** The world the player was last looking at: 'realm' or 'city'. */
  at: 'realm' | 'city';
  /** The strategic world, when this is a campaign. */
  realm: SaveGame | null;
  /** The capital, when this is a campaign; otherwise the standalone run. */
  city: SaveGame | null;
  /**
   * The player's **other** settlements, one per holding they have walked into.
   *
   * Absent in a version-1 envelope, which is not a hole: a campaign written
   * before holdings could be entered had exactly one town and it is in `city`.
   * Each is an ordinary `SaveGame` through `src/sim/save.ts`, carrying its own
   * province in `config.province` — so nothing here needs to know what a
   * province is, and a town that fails to round-trip fails the same round-trip
   * test the capital does.
   */
  towns?: SaveGame[];
  /** Which holding the player was standing in, when `at` is 'city'. */
  atProvince?: number;
}

export interface LoadedGame {
  realm: World | null;
  city: World | null;
  at: 'realm' | 'city';
  /**
   * Every settlement world in the save **except** the one in `city`, in the
   * order it was written. `main.ts` folds the two together into one map keyed
   * by province.
   */
  towns: World[];
  /** The province `city` stands on, or -1 when it is a standalone run. */
  atProvince: number;
}

function isEnvelope(v: unknown): v is SaveEnvelope {
  return typeof v === 'object' && v !== null && 'envelope' in v;
}

// ── Which backend, and which slot ────────────────────────────────────────────

/**
 * The browser store exists from the first line, before `initStorage()` has had
 * a chance to run. That is not a fallback so much as the truth: in a browser it
 * is the answer, and on the desktop it is what the migration reads *from*.
 *
 * It is built lazily because this module is imported by tests that run in Node,
 * where touching `localStorage` at import time would throw.
 */
let browser: SaveStore | null = null;
let store: SaveStore | null = null;
let file: FileStore | null = null;
let active = DEFAULT_SLOT;
let migrated = false;

function browserStore(): SaveStore {
  if (!browser) {
    // Node, or a browser with storage switched off. A store that forgets
    // everything is better than one that throws on every autosave.
    const forgetful = { getItem: () => null, setItem: () => {}, removeItem: () => {} };
    const kv = typeof localStorage !== 'undefined' ? localStorage : forgetful;
    browser = new LocalStore(kv, SAVE_KEY);
  }
  return browser;
}

function current(): SaveStore {
  return store ?? browserStore();
}

export interface StorageStatus {
  kind: 'browser' | 'file';
  /** Where saves are going, in words a player can act on. */
  location: string;
  /** True when saves sit beside the executable rather than in app data. */
  portable: boolean;
  /** Set when the portable folder was not writable and we moved. */
  note?: string;
}

let status: StorageStatus = { kind: 'browser', location: 'browser storage', portable: false };

/** Where saves are going. Shown on the menu, because the player should know. */
export function storageStatus(): StorageStatus {
  return status;
}

/**
 * Choose the backend, and rescue anything already in the browser.
 *
 * Called once from `boot()`, awaited before the menu is drawn — the menu asks
 * what is saved, and asking before the disk has been read would show "no save"
 * to a player who has one. In a browser this returns on the first line.
 */
export async function initStorage(): Promise<StorageStatus> {
  if (!isDesktop()) {
    store = browserStore();
    return status;
  }
  const found = await desktopSaveDir();
  if (!found) {
    // A desktop shell whose disk we cannot use. `localStorage` still works
    // inside the webview, so the game saves — it just saves small.
    store = browserStore();
    status = {
      kind: 'browser',
      location: 'browser storage',
      portable: false,
      note: 'No writable folder was found, so saves are in the window’s own storage and limited to a few megabytes.',
    };
    return status;
  }
  return adoptFileStore(found);
}

/**
 * Install the file backend and carry across whatever is in the browser.
 *
 * Split out of `initStorage()` and exported for one reason: everything above it
 * needs a Tauri window to run, and this project cannot build one without a Rust
 * toolchain. This half — the backend swap, the wording the player is shown, and
 * the migration, which is the part a player's campaign depends on — takes a
 * directory and a filesystem and nothing else, so `tests/storage-facade.test.ts`
 * runs it against a real temp folder with a stand-in `localStorage` behind it.
 *
 * That is not a seam invented for the test. It is the honest shape: which
 * folder to use is a platform question, and what to do once you have one is not.
 */
export async function adoptFileStore(found: DesktopSaveDir): Promise<StorageStatus> {
  const fileStore = new FileStore(found.fs, found.dir, found.location);
  await fileStore.open();
  file = fileStore;
  store = fileStore;
  status = {
    kind: 'file',
    location: found.location,
    portable: found.portable,
    note: found.portable
      ? undefined
      : 'The game’s own folder is not writable — probably it sits under Program Files — so saves went to your app data instead.',
  };
  const m = migrateToFiles(browserStore(), fileStore);
  migrated = m.moved;
  if (m.moved) await fileStore.flush();
  return status;
}

/** True when this boot carried a browser save onto the disk. Menu tells them. */
export function didMigrate(): boolean {
  return migrated;
}

/** Which slot save/load use when nobody says otherwise. */
export function activeSlot(): number {
  return active;
}

export function setActiveSlot(slot: number): void {
  if (isSlot(slot)) active = slot;
}

/** Resolves when every queued write has reached the disk. */
export function flushSaves(): Promise<WriteResult> {
  return current().flush();
}

/** True while bytes are still on their way to the disk — for a close guard. */
export function savesPending(): boolean {
  return file?.busy ?? false;
}

// ── Reading and writing ──────────────────────────────────────────────────────

/**
 * Write whatever the game currently is.
 *
 * `realm` is null for a standalone run, in which case this degrades to exactly
 * what it always was: one world in one slot.
 */
export function writeGame(
  city: World | null,
  realm: World | null,
  at: 'realm' | 'city',
  slot: number = active,
  /**
   * The player's other towns. Whichever of them is `city` is dropped rather
   * than written twice — the caller holds one map of every settlement and
   * should not have to remember which of them is on screen. A town written
   * twice would load as two worlds sharing a province, and the second would
   * quietly become the one the player builds in.
   */
  towns: readonly World[] = [],
): WriteResult {
  try {
    // The wall clock is this layer's business, not the simulation's: `saveWorld`
    // is pure and takes the timestamp rather than reading one.
    const now = Date.now();
    const env: SaveEnvelope = {
      envelope: ENVELOPE_VERSION,
      savedAt: now,
      at,
      realm: realm ? saveWorld(realm, now) : null,
      city: city ? saveWorld(city, now) : null,
      towns: towns.filter((t) => t !== city && t !== realm).map((t) => saveWorld(t, now)),
      atProvince: city?.config.province ?? -1,
    };
    const json = JSON.stringify(env);
    const r = current().write(isSlot(slot) ? slot : active, json);
    if (r.ok && isSlot(slot)) active = slot;
    return r;
  } catch (e) {
    // Quota on the browser backend; anything at all on the way to JSON.
    return { ok: false, bytes: 0, error: e instanceof Error ? e.message : String(e) };
  }
}

/** Back-compatible single-world write, for standalone runs. */
export function writeToStorage(w: World): WriteResult {
  return writeGame(w, null, 'city');
}

function parse(slot: number): unknown {
  const json = current().read(slot);
  if (!json) return null;
  try {
    return JSON.parse(json) as unknown;
  } catch {
    return null;
  }
}

export function readGame(slot: number = active): LoadedGame | null {
  try {
    const raw = parse(isSlot(slot) ? slot : active);
    if (raw === null) return null;
    if (isSlot(slot)) active = slot;
    if (isEnvelope(raw)) {
      const city = raw.city ? loadWorld(raw.city) : null;
      return {
        realm: raw.realm ? loadWorld(raw.realm) : null,
        city,
        at: raw.at === 'realm' ? 'realm' : 'city',
        // `?? []` is the whole of the version-1 migration: an envelope written
        // before a crown could hold two towns has one, and it is in `city`.
        towns: (raw.towns ?? []).map((t) => loadWorld(t)),
        atProvince: raw.atProvince ?? city?.config.province ?? -1,
      };
    }
    // A save from before the envelope: one world, and it might be either kind.
    const w = loadWorld(raw as SaveGame);
    return w.campaign
      ? { realm: w, city: null, at: 'realm', towns: [], atProvince: -1 }
      : { realm: null, city: w, at: 'city', towns: [], atProvince: -1 };
  } catch {
    return null;
  }
}

/** Single-world read, kept for the paths that only ever want one. */
export function readFromStorage(): World | null {
  const g = readGame();
  if (!g) return null;
  return g.at === 'realm' ? (g.realm ?? g.city) : (g.city ?? g.realm);
}

export function hasSave(slot: number = active): boolean {
  return current().read(slot) !== null;
}

export function clearSave(slot: number = active): void {
  current().remove(slot);
}

export interface SaveSummary {
  slot: number;
  wave: number;
  faction: string;
  savedAt: number;
  turn: number;
  campaign: boolean;
}

/**
 * Summaries already worked out, keyed by the exact bytes they came from.
 *
 * A save is several megabytes and `JSON.parse` of one is not free. There was
 * one slot, so the title screen paid for one parse; there are five now, and
 * `listSaves()` would otherwise parse every one of them on every menu render —
 * a screen the player leaves and comes back to. Keying on the raw string rather
 * than on the slot number means a slot that has been written to since is
 * re-read rather than remembered wrongly.
 */
const summaries = new Map<number, { raw: string; summary: SaveSummary | null }>();

/** Describe a stored save without loading it, for the menu. */
export function peekSave(slot: number = active): SaveSummary | null {
  const n = isSlot(slot) ? slot : active;
  const json = current().read(n);
  if (json === null) return null;
  const seen = summaries.get(n);
  if (seen && seen.raw === json) return seen.summary;
  const summary = readSummary(n, json);
  summaries.set(n, { raw: json, summary });
  return summary;
}

function readSummary(slot: number, json: string): SaveSummary | null {
  let raw: unknown;
  try {
    raw = JSON.parse(json) as unknown;
  } catch {
    return null;
  }
  try {
    if (isEnvelope(raw)) {
      const city = raw.city;
      const realm = raw.realm;
      const head = city ?? realm;
      if (!head) return null;
      return {
        slot,
        wave: city?.wave ?? 0,
        faction: head.config.factionId,
        savedAt: raw.savedAt,
        turn: realm?.campaign?.turn ?? 0,
        campaign: realm !== null,
      };
    }
    const s = raw as SaveGame;
    return {
      slot,
      wave: s.wave,
      faction: s.config.factionId,
      savedAt: s.savedAt,
      turn: s.campaign?.turn ?? 0,
      campaign: Boolean(s.campaign),
    };
  } catch {
    return null;
  }
}

/**
 * Every slot that holds something, newest first.
 *
 * There is one save slot in this game because a `localStorage` quota only ever
 * had room for one, not because anyone decided a player should not keep two
 * campaigns going. Files make the choice free, so the constraint goes.
 */
export function listSaves(): SaveSummary[] {
  const out: SaveSummary[] = [];
  for (const slot of current().filled()) {
    const s = peekSave(slot);
    if (s) out.push(s);
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

/** The slot `Continue` should resume: the most recently written one. */
export function newestSlot(): number | null {
  const all = listSaves();
  return all.length > 0 ? all[0].slot : null;
}
